import { Component, forwardRef, Input, OnInit, SimpleChanges, input, output, viewChild } from "@angular/core";
import { NgForm, NgModel, NG_VALUE_ACCESSOR, FormsModule } from "@angular/forms";
import { SelectItem } from "primeng/api";
import { Observable, takeUntil } from "rxjs";
import { SubGroup } from "../../../types";
import { Community } from "../../../types/organization";
import { PCPCommunitySelectorBaseComponent } from "../pcp-community-selector.basecomponent";

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { MultiSelectModule } from "primeng/multiselect";
import { FluidModule } from "primeng/fluid";


@Component({
	selector: "pcp-community-multiselect",
	templateUrl: "./pcp-community-multi-select.component.html",
	styleUrls: [],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => PCPCommunityMultiSelectComponent),
		}
	],
	standalone: true,
	imports: [MultiSelectModule, FormsModule, FluidModule, ErrormessageComponent]
})
export class PCPCommunityMultiSelectComponent extends PCPCommunitySelectorBaseComponent {
	readonly communitySelection = viewChild<NgModel>("communitySelection");

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set communities(communities: Community[]) {
		if (!communities || communities.length === 0) { return; }

		this._communities = communities;
		this.communityOptions = this.communitiesToOptions(communities);
	}

	get communities(): Community[] {
		return this._communities;
	}

	_communities: Community[] = [];

	_observable: Observable<Community[]>;

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set communitiesObservable(observable: Observable<Community[]>) {
		this._observable = observable;
		this.reloadOptions();
	};

	get communitiesObservable(): Observable<Community[]> {
		return this._observable;
	}

	readonly appendTo = input<string>(undefined);
	readonly placeholder = input<string>("Communities");
	readonly maxSelectedLabels = input<number>(5);
	readonly showToggleAll = input<boolean>(true);
	readonly xOfManyErrors = input<boolean>(false);

	// Two-way binding for selectedCommunities
	_selectedCommunities: Community[];

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set selectedCommunities(communities: Community[]) {
		this._selectedCommunities = communities;
		this.propagateChange(this._selectedCommunities);
	};

	get selectedCommunities(): Community[] {
		return this._selectedCommunities;
	}

	readonly selectedCommunitiesChange = output<Community[]>();
	readonly communitiesLoaded = output<any>();

	allCommunities: Community[] = [];
	communityOptions: SelectItem[] = [];
	loadingText: string = "Loading...";

	get label(): string {
		if (this.isLoading) return this.loadingText;

		return this.placeholder();
	}

	constructor() {
		super();
	}

	writeValue(obj: any): void {
		this.selectedCommunities = obj;
	}

	isSingleSelect(): boolean {
		return this.selectionLimit() === 1;
	}

	onPanelHide() {
		const sortedCommunities = this.sortSelectedTop(this._communities, this.selectedCommunities);
		this.communityOptions = this.communitiesToOptions(sortedCommunities);
		this.selectedCommunitiesChange.emit(this.selectedCommunities);
	}

	selectCommunityById(orgId: number) {
		const selectedCommunity = this._communities.find((community: Community) => community.orgId === orgId);
		if (!selectedCommunity) { throw new TypeError(`PCPCommunityMultiSelectComponent.selectCommunityById(${orgId}) : community not found.`); }

		if (this.isSingleSelect()) {
			this.clearSelectedCommunities();
		}

		this.selectCommunity(selectedCommunity);
	}

	reloadOptions(): void {
		if (!this.communitiesObservable) { return; }

		this.isLoading = true;
		this.communitiesObservable
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((communities) => {
				const communitiesWithDisplayText = communities.map((community) => {
					community.displayText = this.getCommunityDisplayText(community);
					return community;
				});
				this.allCommunities = communitiesWithDisplayText;
				this.communities = communitiesWithDisplayText;
				this.isLoading = false;
				// TODO: The 'emit' function requires a mandatory any argument
				this.communitiesLoaded.emit(null);
			});
	}

	selectAllCommunities() {
		this.selectedCommunities = this.communities;
		this.selectedCommunitiesChange.emit(this.selectedCommunities);
	}

	filterOptionsBySubGroup(subGroup: SubGroup) {
		this.communities = this.communitiesBySubGroup(this.allCommunities, subGroup);
	}

	showAllOptions() {
		this.communities = this.allCommunities;
	}

	allCommunitiesAreSelected() {
		return this.selectedCommunities && this.communities && this.selectedCommunities.length === this.communities.length;
	}

	private selectCommunity(community: Community) {
		this.selectedCommunities.push(community);
	}

	private clearSelectedCommunities(): void {
		this.selectedCommunities = [];
	}
}
